import React, { useMemo, useRef, useCallback, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

/**
 * RichTextEditor - Editor WYSIWYG basato su React-Quill
 * Salva il contenuto come HTML nel database
 * 
 * NOTA: Usare `key` prop per forzare il re-render quando cambia il record
 */
const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = '',
  disabled = false,
  readOnly = false,
  className = '',
  minHeight = '120px',
  maxHeight = '400px',
  themeColor = 'emerald',
  error = null,
  label = '',
  required = false,
  id = 'rich-text-editor'
}) => {
  const quillRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Configurazione toolbar
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  }), []);

  // Formati supportati (nota: 'list' copre sia 'ordered' che 'bullet')
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'indent',
    'link'
  ];

  const handleChange = (content, delta, source, editor) => {
    // CRITICAL: Solo modifiche dell'utente, ignorare riformattazioni interne di Quill.
    // Senza questo check, un valore plain-text viene convertito in HTML da Quill,
    // scatena onChange → setState → re-render → Quill riformatta → onChange → loop infinito.
    if (source !== 'user') return;

    if (onChange) {
      // Se il contenuto è solo un tag <p> vuoto, passa stringa vuota
      const plainText = editor.getText().trim();
      onChange(plainText ? content : '');
    }
  };

  return (
    <div className={`rich-text-editor-wrapper ${className}`}>
      {label && (
        <label 
          htmlFor={id}
          className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
        >
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div 
          className={`
            rich-text-editor-container
            border rounded-md overflow-hidden
            ${disabled || readOnly 
              ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-70' 
              : `border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 
                 focus-within:ring-2 focus-within:ring-${themeColor}-500 focus-within:border-${themeColor}-500`
            }
            ${error ? 'border-red-500 dark:border-red-400' : ''}
            ${isExpanded ? 'rich-text-editor-expanded' : ''}
          `}
          style={isExpanded ? { minHeight } : { minHeight, maxHeight }}
        >
          <ReactQuill
            ref={quillRef}
            id={id}
            theme="snow"
            value={value || ''}
            onChange={handleChange}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
            readOnly={disabled || readOnly}
            className="rich-text-quill"
          />
        </div>
        <button
          type="button"
          onClick={toggleExpand}
          title={isExpanded ? 'Comprimi editor' : 'Espandi editor'}
          className="absolute bottom-1 right-1 z-10 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
        >
          {isExpanded ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 110-2h4a1 1 0 011 1v4a1 1 0 11-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 112 0v1.586l2.293-2.293a1 1 0 011.414 1.414L6.414 15H8a1 1 0 110 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 110-2h1.586l-2.293-2.293a1 1 0 011.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      </div>
      
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>
      )}
      
      {/* Stili inline per il dark mode e personalizzazioni */}
      <style>{`
        .rich-text-editor-container .ql-toolbar {
          border: none;
          border-bottom: 1px solid;
          border-color: inherit;
          background: rgba(0, 0, 0, 0.02);
        }
        
        .dark .rich-text-editor-container .ql-toolbar {
          background: rgba(255, 255, 255, 0.02);
        }
        
        .rich-text-editor-container .ql-container {
          border: none;
          font-size: 0.875rem;
          min-height: ${minHeight};
          max-height: calc(${maxHeight} - 42px);
          overflow-y: auto;
        }
        
        .rich-text-editor-container.rich-text-editor-expanded .ql-container {
          max-height: none;
        }
        
        .rich-text-editor-container .ql-editor {
          min-height: calc(${minHeight} - 42px);
          color: inherit;
        }
        
        .dark .rich-text-editor-container .ql-editor {
          color: #e5e7eb;
        }
        
        .rich-text-editor-container .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        
        .dark .rich-text-editor-container .ql-editor.ql-blank::before {
          color: #6b7280;
        }
        
        /* Toolbar icons dark mode */
        .dark .rich-text-editor-container .ql-toolbar .ql-stroke {
          stroke: #9ca3af;
        }
        
        .dark .rich-text-editor-container .ql-toolbar .ql-fill {
          fill: #9ca3af;
        }
        
        .dark .rich-text-editor-container .ql-toolbar .ql-picker {
          color: #9ca3af;
        }
        
        .dark .rich-text-editor-container .ql-toolbar button:hover .ql-stroke,
        .dark .rich-text-editor-container .ql-toolbar .ql-picker-label:hover .ql-stroke {
          stroke: #fff;
        }
        
        .dark .rich-text-editor-container .ql-toolbar button:hover .ql-fill,
        .dark .rich-text-editor-container .ql-toolbar .ql-picker-label:hover .ql-fill {
          fill: #fff;
        }
        
        .dark .rich-text-editor-container .ql-toolbar button.ql-active .ql-stroke {
          stroke: #10b981;
        }
        
        .dark .rich-text-editor-container .ql-toolbar button.ql-active .ql-fill {
          fill: #10b981;
        }
        
        /* Dropdown dark mode */
        .dark .ql-picker-options {
          background-color: #374151;
          border-color: #4b5563;
        }
        
        .dark .ql-picker-item {
          color: #e5e7eb;
        }
        
        .dark .ql-picker-item:hover {
          color: #fff;
          background-color: #4b5563;
        }
        
        /* Link tooltip dark mode */
        .dark .ql-tooltip {
          background-color: #374151;
          border-color: #4b5563;
          color: #e5e7eb;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
        }
        
        .dark .ql-tooltip input[type=text] {
          background-color: #1f2937;
          border-color: #4b5563;
          color: #e5e7eb;
        }
        
        .dark .ql-tooltip a {
          color: #10b981;
        }
        
        /* Scrollbar styling */
        .rich-text-editor-container .ql-container::-webkit-scrollbar {
          width: 6px;
        }
        
        .rich-text-editor-container .ql-container::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .rich-text-editor-container .ql-container::-webkit-scrollbar-thumb {
          background-color: #d1d5db;
          border-radius: 3px;
        }
        
        .dark .rich-text-editor-container .ql-container::-webkit-scrollbar-thumb {
          background-color: #4b5563;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
